import { Search, ArrowUpDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PackageFilterBarProps {
  logisticsManagers?: { id: string; name: string }[];
  selectedAssignee?: string;
  onAssigneeChange?: (value: string) => void;
  selectedStatus?: string;
  onStatusChange?: (value: string) => void;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  sortBy?: string;
  onSortChange?: (value: string) => void;
  visibleUserCount?: number;
}

const statusOptions = [
  { value: "all", label: "All Active" },
  { value: "pending-review", label: "Pending Review" },
  { value: "awaiting-confirmation", label: "Awaiting Confirmation" },
  { value: "pickup-scheduled", label: "Pickup Scheduled" },
  { value: "picked-up", label: "Picked Up" },
  { value: "user-canceled", label: "Canceled by User" },
  { value: "archived", label: "Archived" },
];

const sortOptions = [
  { value: "pickup-date-asc", label: "Pickup Date (Soonest)" },
  { value: "pickup-date-desc", label: "Pickup Date (Latest)" },
  { value: "approval-approved-first", label: "Approved" },
  { value: "approval-not-approved-first", label: "Not Approved" },
];

export const PackageFilterBar = ({
  logisticsManagers = [],
  selectedAssignee = "all",
  onAssigneeChange,
  selectedStatus = "all",
  onStatusChange,
  searchQuery = "",
  onSearchChange,
  sortBy = "pickup-date-asc",
  onSortChange,
  visibleUserCount,
}: PackageFilterBarProps) => {
  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-muted/30 rounded-lg border">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          {/* Assigned To Dropdown */}
          <Select value={selectedAssignee} onValueChange={onAssigneeChange}>
            <SelectTrigger className="w-[200px] bg-background">
              <SelectValue placeholder="Assigned To" />
            </SelectTrigger>
            <SelectContent className="bg-popover">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {logisticsManagers.map((manager) => (
                <SelectItem key={manager.id} value={manager.id}>
                  {manager.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Sort Dropdown */}
          <Select value={sortBy} onValueChange={onSortChange}>
            <SelectTrigger className="w-[200px] bg-background">
              <div className="flex items-center gap-1">
                <ArrowUpDown className="h-3 w-3 shrink-0" />
                <SelectValue placeholder="Sort by" />
              </div>
            </SelectTrigger>
            <SelectContent className="bg-popover">
              {sortOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Search Input */}
        <div className="relative w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search users"
            value={searchQuery}
            onChange={(e) => onSearchChange?.(e.target.value)}
            className="pl-9 w-full sm:w-[280px] bg-background"
          />
        </div>
      </div>

      {/* Visible user count */}
      {visibleUserCount !== undefined && (
        <p className="text-sm text-muted-foreground px-1">
          Showing {visibleUserCount} user{visibleUserCount !== 1 ? "s" : ""}
        </p>
      )}
    </div>
  );
};
